import React, { useEffect, useState } from "react";
import axios from "axios";
import OrderCard from "./OrderCard";
import { FaFilter, FaTimes, FaSearch } from "react-icons/fa";
import { ECWID_STORE_ID, ECWID_ACCESS_TOKEN } from "../config/EcwidConfig";

const mapStatus = (status) => {
  if (typeof status !== "string") return "Unknown";
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const statusOptions = [
  "AWAITING_PAYMENT",
  "PAID",
  "SHIPPED",
  "DELIVERED",
  "CANCELLED",
];

const timeOptions = ["Last 30 Days", "2025", "2024", "2023", "2022", "Older"];

const YourOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // const storeId = "88026380";
  // const token = "secret_kRcPTGe3QT5ra6F4Jn2mP9xwuGk2ZbCL";
  // const token = "secret_Jx3TRcj9PWcuhpjsygNwRfkpyUytxtDg";

  useEffect(() => {
    // Check if user is logged in
    const customerProfile = localStorage.getItem("customerProfile");
    if (customerProfile) {
      try {
        const customerData = JSON.parse(customerProfile);
        setCustomer(customerData);
      } catch (e) {
        console.error("Error parsing customer data:", e);
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!customer) return;

      try {
        // Fetch orders for the specific customer using their ID
        const response = await axios.get(
          `https://app.ecwid.com/api/v3/${ECWID_STORE_ID}/orders?customerId=${customer.id}`,
          {
            headers: { Authorization: `Bearer ${ECWID_ACCESS_TOKEN}` },
          }
        );
        const customerOrders = response.data.items || [];
        setOrders(customerOrders);
        setFilteredOrders(customerOrders);
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };

    if (customer) {
      fetchOrders();
    }
  }, [customer]);

  useEffect(() => {
    let filtered = [...orders];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (order) =>
          order.id.toString().includes(searchTerm) ||
          order.items.some((item) =>
            item.name.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
    }

    // Apply status filters
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(
        (order) =>
          selectedStatuses.includes(order.paymentStatus) ||
          selectedStatuses.includes(order.fulfillmentStatus)
      );
    }

    // Apply time filters
    const now = new Date();
    if (selectedTimes.length > 0) {
      filtered = filtered.filter((order) => {
        const orderDate = new Date(order.createDate);
        return selectedTimes.some((time) => {
          if (time === "Last 30 Days") {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(now.getDate() - 30);
            return orderDate >= thirtyDaysAgo;
          }
          if (["2025", "2024", "2023", "2022"].includes(time)) {
            return orderDate.getFullYear().toString() === time;
          }
          if (time === "Older") {
            return orderDate.getFullYear() < 2022;
          }
          return true;
        });
      });
    }

    setFilteredOrders(filtered);
  }, [selectedStatuses, selectedTimes, orders, searchTerm]);

  const handleStatusChange = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleTimeChange = (time) => {
    setSelectedTimes((prev) =>
      prev.includes(time) ? prev.filter((t) => t !== time) : [...prev, time]
    );
  };

  const clearFilters = () => {
    setSelectedStatuses([]);
    setSelectedTimes([]);
    setSearchTerm("");
  };

  const activeFiltersCount = selectedStatuses.length + selectedTimes.length;

  // Show message if user is not logged in
  if (!isLoading && !customer) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen py-10 px-4 md:px-0">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-100">
            <div className="text-5xl text-gray-300 mb-4">🔒</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Please Sign In to View Orders
            </h3>
            <p className="text-gray-600 mb-6">
              You need to be logged in to view your order history.
            </p>
            <a
              href="/SignIn"
              className="px-6 py-2 bg-[#82b016] hover:bg-[#6fa010] text-white font-medium rounded-lg transition-colors duration-200"
            >
              Sign In
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen py-10 px-4 md:px-0">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-100">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#82b016] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your orders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen py-10 px-4 md:px-0">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-2">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
            Your Orders
          </h1>
          <p className="text-gray-600">Manage and track your order history</p>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-gradient-to-r from-[#638d02] via-[#82b016] to-[#638d02] rounded-xl shadow-lg p-4 mb-6 border border-gray-100">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-lime-600" />
              </div>
              <input
                type="text"
                placeholder="Search by order ID or product name..."
                className="w-full pl-10 pr-4 py-2 border border-[#82b016] rounded-lg ring-2 ring-[#82b016] focus:outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200"
            >
              <FaFilter className="text-lime-600" /> Filters
              {activeFiltersCount > 0 && (
                <span className="bg-[#82b016] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            {/* Clear Filters Button */}
            {(activeFiltersCount > 0 || searchTerm) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 font-medium rounded-lg transition-colors duration-200"
              >
                <FaTimes /> Clear
              </button>
            )}
          </div>

          {/* Mobile Filter Panel */}
          {isFilterOpen && (
            <div className="mt-4 md:hidden">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-gray-800 font-medium mb-2">
                      Order Status
                    </h4>
                    <div className="space-y-2">
                      {statusOptions.map((status) => (
                        <label
                          key={status}
                          className="flex items-center gap-2 text-gray-700"
                        >
                          <input
                            type="checkbox"
                            className="form-checkbox h-4 w-4 text-[#82b016]"
                            checked={selectedStatuses.includes(status)}
                            onChange={() => handleStatusChange(status)}
                          />
                          <span className="text-sm">{mapStatus(status)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-gray-800 font-medium mb-2">
                      Order Time
                    </h4>
                    <div className="space-y-2">
                      {timeOptions.map((time) => (
                        <label
                          key={time}
                          className="flex items-center gap-2 text-gray-700"
                        >
                          <input
                            type="checkbox"
                            className="form-checkbox h-4 w-4 text-[#82b016]"
                            checked={selectedTimes.includes(time)}
                            onChange={() => handleTimeChange(time)}
                          />
                          <span className="text-sm">{time}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Desktop Filter Sidebar */}
          <aside className="hidden lg:block w-full lg:w-64">
            <div className="sticky top-6">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-500">Filters</h3>
                {activeFiltersCount > 0 && (
                  <span className="bg-[#82b016] text-white text-xs rounded-full px-2 py-1">
                    {activeFiltersCount} active
                  </span>
                )}
              </div>

              {/* Order Status */}
              <div className="mb-2">
                <h4 className="bg-gray-500 h-12 rounded-lg relative bottom-[-12px] z-[-1] text-white font-medium flex items-start ps-2 pt-1 gap-2">
                  Order Status
                </h4>
                <div className="bg-gray-200 rounded-lg border border-gray-300 p-4">
                  {statusOptions.map((status) => (
                    <label
                      key={status}
                      className="flex items-center gap-3 text-gray-700 hover:bg-gray-50 p-2 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="form-checkbox h-4 w-4 text-[#82b016] rounded"
                        checked={selectedStatuses.includes(status)}
                        onChange={() => handleStatusChange(status)}
                      />
                      <span className="text-sm">{mapStatus(status)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Order Time */}
              <div className="mb-6">
                <h4 className="bg-gray-500 h-12 rounded-lg relative bottom-[-12px] z-[-1] text-white font-medium flex items-start ps-2 pt-1 gap-2">
                  Order Time
                </h4>
                <div className="bg-gray-200 rounded-lg border border-gray-300 p-4">
                  {timeOptions.map((time) => (
                    <label
                      key={time}
                      className="flex items-center gap-3 text-gray-700 hover:bg-gray-50 p-2 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="form-checkbox h-4 w-4 text-[#82b016] rounded"
                        checked={selectedTimes.includes(time)}
                        onChange={() => handleTimeChange(time)}
                      />
                      <span className="text-sm">{time}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Clear Filters */}
              {(activeFiltersCount > 0 || searchTerm) && (
                <button
                  onClick={clearFilters}
                  className="w-full mt-4 bg-red-50 hover:bg-red-100 text-red-600 py-2 rounded-lg transition-colors duration-200 text-sm font-medium"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          </aside>

          {/* Orders Section */}
          <main className="flex-1 overflow-y-auto h-[700px]">
            {filteredOrders.length > 0 ? (
              <div className="">
                <p className="text-gray-600 mb-4">
                  Showing {filteredOrders.length} of {orders.length} orders
                </p>
                {filteredOrders.map((order) => {
                  // Calculate total dynamically if not provided
                  const total =
                    order.total ||
                    order.items.reduce(
                      (sum, item) => sum + item.price * item.quantity,
                      0
                    );

                  return (
                    <OrderCard
                      key={order.id}
                      order={order}
                      paymentStatus={mapStatus(order.paymentStatus)}
                      fulfillmentStatus={mapStatus(order.fulfillmentStatus)}
                      total={total} // Pass the total to the OrderCard
                      paymentMethod={order.paymentMethod || "N/A"} // Add paymentMethod
                      imageUrl={
                        order.items[0]?.imageUrl || "/assets/No-img.png.png"
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-100">
                <div className="text-5xl text-gray-300 mb-4">📦</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  No Orders Found
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm || activeFiltersCount > 0
                    ? "No orders match your current filters. Try adjusting your search or filters."
                    : "You haven't placed any orders yet."}
                </p>
                {(searchTerm || activeFiltersCount > 0) && (
                  <button
                    onClick={clearFilters}
                    className="px-6 py-2 bg-[#82b016] hover:bg-[#6fa010] text-white font-medium rounded-lg transition-colors duration-200"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default YourOrders;
